import { useCallback, useRef, useState } from 'react';
import { coalesceVeilcasterWeaponStealFlyBatch } from './coalesceVeilcasterWeaponStealFlyBatch';

function mergeGrantQueue(prev, grants) {
  if (!grants?.length) {
    return prev;
  }
  const existingKeys = new Set(prev.map((g) => g.key));
  const merged = [...prev];
  grants.forEach((grant) => {
    if (!existingKeys.has(grant.key)) {
      merged.push(grant);
    }
  });
  return merged;
}

/**
 * Server-authoritative Veilcaster (168) weapon steal fly animations.
 */
export const useVeilcasterWeaponStealFlyAnimations = () => {
  const [activeFlyGrants, setActiveFlyGrants] = useState([]);
  const consumedFlyAnimationKeysRef = useRef(new Set());

  const queueGrants = useCallback((grants) => {
    if (!grants?.length) {
      return;
    }

    const grantsToQueue = [];
    grants.forEach((grant) => {
      if (!grant?.key) {
        throw new Error('useVeilcasterWeaponStealFlyAnimations.queueGrants: grant key is required');
      }
      if (consumedFlyAnimationKeysRef.current.has(grant.key)) {
        return;
      }
      consumedFlyAnimationKeysRef.current.add(grant.key);
      grantsToQueue.push(grant);
    });

    const coalescedGrants = coalesceVeilcasterWeaponStealFlyBatch(grantsToQueue);
    coalescedGrants.forEach((coalescedGrant) => {
      consumedFlyAnimationKeysRef.current.add(coalescedGrant.key);
    });

    if (coalescedGrants.length === 0) {
      return;
    }

    setActiveFlyGrants((prev) => mergeGrantQueue(prev, coalescedGrants));
  }, []);

  const dismissFlyGrant = useCallback((key) => {
    setActiveFlyGrants((prev) => prev.filter((g) => g.key !== key));
  }, []);

  return {
    activeFlyGrants,
    dismissFlyGrant,
    queueGrants,
  };
};

export default useVeilcasterWeaponStealFlyAnimations;
